import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { VxSwal } from '@voxie/frontend-components';
import axios from 'axios';
import dayjs from 'dayjs';
import { escapeHtml } from '~/utils/html';
import { stringifyQueryString } from '~/utils/string';

axios.defaults.headers.common = {
    'X-Requested-With': 'XMLHttpRequest',
};

let popupShown = false;

const showSessionExpiredPopup = () => {
    if (popupShown) {
        return;
    }

    popupShown = true;
    VxSwal.fire({
        title: 'Session Expired',
        icon: faTriangleExclamation,
        iconColor: 'warning',
        html: 'Your session has expired. Please reload page to continue.',
        confirmButtonText: 'Reload Page',
    }).then(() => {
        localStorage.setItem('page_last_reloaded_at', Date.now());
        window.location.reload();
    });
};

const currentPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const ssoRequiredHtml = (reason, teamName) => {
    if (reason === 'expired') {
        return `Your single sign-on session for <strong>${escapeHtml(teamName)}</strong> has expired.`;
    }

    if (reason === 'revoked') {
        return `Your identity provider revoked your single sign-on access to <strong>${escapeHtml(teamName)}</strong>.`;
    }

    return `<strong>${escapeHtml(teamName)}</strong> requires single sign-on.`;
};

const showSsoRequiredPopup = ({ reason, team_name: teamName, login_url: loginUrl }) => {
    if (popupShown) {
        return;
    }

    popupShown = true;
    VxSwal.fire({
        title: reason === 'expired' ? 'Session Expired' : 'Single Sign-On Required',
        icon: faTriangleExclamation,
        iconColor: 'warning',
        html: ssoRequiredHtml(reason, teamName),
        confirmButtonText: 'Log In with SSO',
        allowOutsideClick: false,
    }).then(() => {
        window.location.assign(`${loginUrl}?${stringifyQueryString({ intended: currentPath() })}`);
    });
};

const ssoRequiredOf = (data) => (typeof data?.sso_required?.login_url === 'string' ? data.sso_required : null);

const setPageLastReloadedAt = () => {
    localStorage.setItem('page_last_reloaded_at', Date.now());
};

const handlePageReload = () => {
    const now = dayjs();
    const pageLastReloadedAt = localStorage.getItem('page_last_reloaded_at');
    const showPopup = !pageLastReloadedAt || dayjs(Number(pageLastReloadedAt)).isAfter(now.subtract(1, 'hour'));

    if (showPopup) {
        showSessionExpiredPopup();
    } else {
        popupShown = true;
        setPageLastReloadedAt();
        window.location.reload();
    }
};

export const handleAuthError = (error) => {
    if (error.response === undefined) {
        return;
    }

    if (error.response.status !== 401) {
        return;
    }

    const ssoRequired = ssoRequiredOf(error.response.data);

    if (ssoRequired !== null) {
        showSsoRequiredPopup(ssoRequired);
    } else if (!['get', 'head', 'options'].includes(error.response.config.method)) {
        showSessionExpiredPopup();
    } else {
        handlePageReload();
    }
};

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        handleAuthError(error);

        return Promise.reject(error);
    }
);

export default axios;
